import { DropdownMenu as M } from "radix-ui";
import { Check, ChevronRight, MoreHorizontal } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Button } from "./button";

export const MenuRoot = M.Root;
export const MenuTrigger = M.Trigger;

export function MenuContent({ children, align = "end", side, sideOffset = 6, className, label }: {
  children: ReactNode;
  align?: "start" | "end" | "center";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  className?: string;
  label?: string;
}) {
  return (
    <M.Portal>
      <M.Content align={align} side={side} sideOffset={sideOffset} className={cn("menu-pop", className)} aria-label={label}>
        {children}
      </M.Content>
    </M.Portal>
  );
}

export function MenuItem({ icon, danger, className, children, ...props }: ComponentProps<typeof M.Item> & { icon?: ReactNode; danger?: boolean }) {
  return (
    <M.Item className={cn("menu-item", danger && "menu-item-danger", className)} {...props}>
      {icon}
      {children}
    </M.Item>
  );
}

/** 单选组（如筛选下拉）：选中项右侧显示 ✓。 */
export const MenuRadioGroup = M.RadioGroup;

export function MenuRadioItem({ className, children, ...props }: ComponentProps<typeof M.RadioItem>) {
  return (
    <M.RadioItem className={cn("menu-item", className)} {...props}>
      {children}
      <M.ItemIndicator className="menu-check">
        <Check className="ico" />
      </M.ItemIndicator>
    </M.RadioItem>
  );
}

/** 二级菜单：触发项右侧显示 ›，hint 为右侧的当前值（如「全部」）。内容过长时可滚动。 */
export const MenuSub = M.Sub;

export function MenuSubTrigger({ icon, hint, className, children, ...props }: ComponentProps<typeof M.SubTrigger> & { icon?: ReactNode; hint?: ReactNode }) {
  return (
    <M.SubTrigger className={cn("menu-item", className)} {...props}>
      {icon}
      {children}
      <span className="menu-hint">{hint}</span>
      <ChevronRight className="ico muted" />
    </M.SubTrigger>
  );
}

export function MenuSubContent({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <M.Portal>
      <M.SubContent className={cn("menu-pop menu-pop-scroll", className)} sideOffset={6} alignOffset={-4} collisionPadding={8} aria-label={label}>
        {children}
      </M.SubContent>
    </M.Portal>
  );
}

export const MenuSep = () => <M.Separator className="menu-sep" />;
export const MenuLabel = ({ children }: { children: ReactNode }) => <M.Label className="menu-label">{children}</M.Label>;

/** 多操作的统一入口：只显示「…」图标的按钮 + 下拉菜单（label 用于读屏和悬停提示）。
 *  size：md 与页头按钮等高，sm 用于表格行 / 卡片；trigger 可替换为自定义按钮，side 控制展开方向。 */
export function ActionsMenu({ children, label = "更多操作", trigger, side, size = "md", variant, align = "end" }: {
  children: ReactNode;
  label?: string;
  trigger?: ReactNode;
  side?: "top" | "bottom" | "right";
  size?: "sm" | "md";
  /** 默认：md 描边（页头），sm 无边框（表格行）；工具栏里的小按钮用 sm + outline。 */
  variant?: "outline" | "ghost";
  align?: "start" | "end";
}) {
  return (
    <MenuRoot>
      <MenuTrigger asChild>
        {trigger ?? (
          <Button variant={variant ?? (size === "md" ? "outline" : "ghost")} size={size === "md" ? "icon" : "icon-sm"} aria-label={label} title={label}>
            <MoreHorizontal className="ico" />
          </Button>
        )}
      </MenuTrigger>
      <MenuContent label={label} side={side} align={align}>
        {children}
      </MenuContent>
    </MenuRoot>
  );
}
